import { useEffect, useRef, type ReactNode } from 'react';
import { X } from './icons';
import { gsap, useGSAP } from '../lib/gsap';

type ModalSize = 'sm' | 'md' | 'lg' | '2xl' | '3xl';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  size?: ModalSize;
  dismissible?: boolean;
  children: ReactNode;
}

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
};

let lockCount = 0;

function lockBodyScroll() {
  if (lockCount === 0) {
    document.documentElement.classList.add('overflow-hidden');
  }
  lockCount += 1;
}

function unlockBodyScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.documentElement.classList.remove('overflow-hidden');
  }
}

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  dismissible = true,
  children,
}: ModalProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const previousActiveRef = useRef<HTMLElement | null>(null);

  // 入场微交互：遮罩淡入 + 面板「升入」。桌面居中缩放升入；移动端为底部抽屉(items-end)故从底部滑入。
  // reduce-motion 下整段跳过(元素恒为自然态,瞬现)。退场不做动画(组件 open=false 即卸载)。
  useGSAP(
    () => {
      if (!open) return;
      const card = cardRef.current;
      const backdrop = backdropRef.current;
      if (!card) return;
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        if (backdrop) gsap.fromTo(backdrop, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2, ease: 'power1.out' });
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        if (isDesktop) {
          gsap.fromTo(
            card,
            { autoAlpha: 0, y: 14, scale: 0.97, transformOrigin: 'center center' },
            { autoAlpha: 1, y: 0, scale: 1, duration: 0.26, ease: 'power2.out', clearProps: 'transform' },
          );
        } else {
          gsap.fromTo(
            card,
            { autoAlpha: 0, yPercent: 14 },
            { autoAlpha: 1, yPercent: 0, duration: 0.3, ease: 'power3.out', clearProps: 'transform' },
          );
        }
      });
      return () => mm.revert();
    },
    { dependencies: [open] },
  );

  useEffect(() => {
    if (!open) return;

    previousActiveRef.current = document.activeElement as HTMLElement | null;
    lockBodyScroll();

    const focusables = cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    const first = focusables?.[0];
    first?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const firstEl = nodes[0];
      const lastEl = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === firstEl || !cardRef.current?.contains(active)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (active === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      unlockBodyScroll();
      const prev = previousActiveRef.current;
      if (prev && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [open, dismissible, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-scrim backdrop-blur-[2px]"
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={cardRef}
        className={`liquid-glass-panel relative w-full ${sizeClass[size]} rounded-t-sheet border border-line max-lg:overflow-hidden sm:rounded-card`}
      >
        <div className="p-8">
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 text-fg-subtle hover:text-fg"
              aria-label="关闭"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          {title && (
            <header className="mb-5">
              <h2 id="modal-title" className="text-base font-semibold text-fg">
                {title}
              </h2>
              {description && <p className="mt-1 text-sm text-fg-subtle">{description}</p>}
            </header>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
